/* eslint-disable react-hooks/exhaustive-deps */
import { ConfirmationModalBase } from "@/components/common/modals";
import StatusPage from "@/components/status-page";
import { useHeaderButton } from "@/contexts/header-button-context";
import {
  useGetFlow,
  usePerformFlowConnectionsCheck,
  useSendFlowToApproval,
  useUpdateFlow,
  useUpdateFlowRevision,
} from "@/queries/flows/flows.api";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import { cn } from "@/utils/theme";
import { WorkflowCanvas } from "@/workflow_api/canvas";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  useToast,
} from "@elaniintc/goes-design-system";
import type { AxiosError } from "axios";
import { useCallback, useEffect, useState, useMemo } from "react";
import {
  useBlocker,
  useNavigate,
  useParams,
  useRevalidator,
} from "react-router";
import EditFlowModal from "../procesos/components/EditFlowModal";
import EditParentFlowModal from "../procesos/components/EditParentFlowModal";
import FlowResolutionModal from "../procesos/components/FlowResolutionModal";
import FlowResolutionDetail from "../procesos/components/FlowResolutionDetail";

interface ApiErrors {
  name?: string[];
}
interface ApiErrorResponse {
  errors?: ApiErrors;
}

const EditFlowPage = () => {
  const params = useParams();
  const { data } = useGetFlow(params.flowId!);
  const { setComp } = useHeaderButton();
  const { showToast } = useToast();
  const { hasPermission } = useAuthorization();
  const canPerformReview = hasPermission(Permissions.flows["perform-review"]);
  const { parseApiNodesToReactFlowNodes, reset, setFlowId, nodes, edges } =
    useWorkflowStore();
  const [pendingReviewStatus, setPendingReviewStatus] = useState<
    "APPROVED" | "DENIED" | null
  >(null);

  const {
    flowName,
    setFlowName,
    reset: resetUi,
    setNodesWithErrors,
    modalState: uimodal,
    setModalState: setUIModalState,
    setSelectedErrors,
    setIsFlowBlocked,
    isFlowBlocked,
    changeCounter,
  } = useUIStore();
  const [modalState, setModalState] = useState<
    | "idle"
    | "save-intent"
    | "edit"
    | "make-revision"
    | "show-resolution-history"
  >("idle");
  const [lastChangeCounter, setLastChangeCounter] = useState(changeCounter);

  const { mutateAsync: updateFlow, isPending: isLoadingUpdateFlow } =
    useUpdateFlow(params.flowId!);

  const shouldBlock = useMemo(() => {
    const nameHasChanged = flowName !== data?.data.name;
    return nameHasChanged;
  }, [flowName, data]);

  const shouldResetToDraft = useMemo(() => {
    if (!data?.data) return false;

    // Para main flow: verificar si hay revisión completada
    const revisionCompleted = data.data.revisions?.some(
      (r) => r.status === "APPROVED" || r.status === "DENIED",
    );

    // Para flujos hijos (sin revisiones): verificar si ya fue validado/rechazado
    const wasValidatedOrRejected =
      data.data.review_status === "APPROVED" ||
      data.data.review_status === "DENIED";

    const result =
      (revisionCompleted || wasValidatedOrRejected) &&
      data.data.review_status !== "NOT_STARTED";
    return result;
  }, [data?.data]);

  const isInApprovalProcess = useMemo(() => {
    return (
      data?.data?.review_status === "PENDING" ||
      data?.data?.review_status === "IN_PROCESS"
    );
  }, [data?.data?.review_status]);

  const blocker = useBlocker(shouldBlock);

  const revalidator = useRevalidator();

  // useEffect(() => {
  //   if (data?.data?.id) {
  //     setLastChangeCounter(changeCounter);
  //   }
  // }, [data?.data?.id]);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (uimodal === "idle" && blocker.state !== "blocked") {
        if ((e.ctrlKey || e.metaKey) && e.key === "f") {
          e.preventDefault();
          setUIModalState("search-nodes");
        }
      }
    };
    document.addEventListener("keydown", down);
    return () => {
      document.removeEventListener("keydown", down);
    };
  }, [uimodal, blocker.state, setUIModalState]);

  useEffect(() => {
    if (changeCounter > lastChangeCounter && shouldResetToDraft) {
      updateFlow({ review_status: "NOT_STARTED" });
      setLastChangeCounter(changeCounter);
    } else if (changeCounter !== lastChangeCounter) {
      setLastChangeCounter(changeCounter);
    }
  }, [changeCounter, lastChangeCounter, shouldResetToDraft, updateFlow]);

  const onSaveFlow = useCallback(
    async ({
      name,
      parent_flow_id,
      max_time_extensions,
    }: {
      name: string;
      parent_flow_id?: string;
      max_time_extensions?: number;
    }) => {
      try {
        await updateFlow({
          ...(name !== flowName
            ? {
                name,
              }
            : {}),
          ...(parent_flow_id
            ? {
                parent_flow_id,
              }
            : {}),
          ...(max_time_extensions !== undefined
            ? {
                max_time_extensions,
              }
            : {}),
        });
        setModalState("idle");
        showToast({
          title: "Cambios guardados",
          description: "Los cambios en el flujo han sido guardados",
          options: {
            type: "success",
            duration: 5000,
          },
        });
        setTimeout(() => {
          revalidator.revalidate();
          // refetch();
        }, 200);
      } catch (error) {
        setError(error as AxiosError<ApiErrorResponse>);
        console.error("Error updating flow:", error);
        showToast({
          title: "Error al guardar el flujo",
          description: "Ha ocurrido un error al intentar guardar el flujo.",
          options: {
            type: "error",
            duration: 5000,
          },
        });
      }
    },
    [revalidator, flowName, updateFlow, showToast],
  );

  useEffect(() => {
    if (data) {
      if (data.data.nodes) {
        parseApiNodesToReactFlowNodes({
          nodes: data.data.nodes,
        });
      }
      setFlowName(data.data.name);
      setFlowId(data.data.id!);
      setNodesWithErrors(new Map());
      setSelectedErrors([]);

      const hasActiveRevision = data.data.revisions?.some(
        (r) => r.status === "PENDING" || r.status === "IN_PROCESS",
      );

      const isInApprovalProcess =
        data.data.review_status === "PENDING" ||
        data.data.review_status === "IN_PROCESS";

      setIsFlowBlocked(
        data.data.status === "PUBLISHED" ||
          data.data.status === "DEPRECATED" ||
          data.data.editing === true ||
          hasActiveRevision === true ||
          isInApprovalProcess === true,
      );
    } else {
      reset();
      resetUi();
      setFlowName("");
      setFlowId("");
    }
    return () => {
      setFlowName("");
      setIsFlowBlocked(false);
    };
  }, [
    data,
    setFlowName,
    resetUi,
    reset,
    parseApiNodesToReactFlowNodes,
    setFlowId,
    setNodesWithErrors,
    setSelectedErrors,
    setIsFlowBlocked,
  ]);

  const {
    mutateAsync: performFlowConnectionsCheck,
    isPending: isPerformingFlowConnectionsCheck,
  } = usePerformFlowConnectionsCheck(params.flowId!);

  const {
    mutateAsync: sendFlowToApproval,
    isPending: isSendingFlowToApproval,
  } = useSendFlowToApproval(params.flowId!);

  const {
    mutateAsync: updateFlowRevision,
    isPending: isLoadingUpdateFlowRevision,
  } = useUpdateFlowRevision(params.flowId!);

  const performStartRevision = useCallback(async () => {
    const lastestRevision = data?.data.revisions?.reduce((prev, current) => {
      return prev.created_at > current.created_at ? prev : current;
    });
    if (!lastestRevision) {
      return;
    }
    try {
      await updateFlowRevision({
        action: "IN_PROCESS",
        revisionId: lastestRevision?.id || "",
      });
      revalidator.revalidate();
      showToast({
        title: "Revisión iniciada",
        description: "La revisión del flujo ha sido iniciada correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    } catch (error) {
      console.error("Error starting flow revision:", error);
      showToast({
        title: "Error al iniciar la revisión del flujo",
        description:
          "Ha ocurrido un error al intentar iniciar la revisión del flujo.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    }
  }, [data]);

  const performConnectionCheck = useCallback(async () => {
    setNodesWithErrors(new Map());
    try {
      await performFlowConnectionsCheck();

      if (isInApprovalProcess) {
        setPendingReviewStatus("APPROVED");
      }
      setUIModalState("validation-success");
    } catch (err) {
      const error = err as AxiosError<{
        errors: {
          [k in string]: {
            message: string;
            type: string;
            node: string;
          }[];
        };
      }>;
      const nodesWithErrors = new Map<
        string,
        {
          message: string;
          type: string;
          node: string;
        }[]
      >();
      if (error?.response?.data.errors) {
        Object.entries(error.response.data.errors).forEach(([nodeId, err]) => {
          nodesWithErrors.set(nodeId, err ? [...err] : []);
        });
        setNodesWithErrors(nodesWithErrors);

        //setPendingReviewStatus("DENIED");

        if (isInApprovalProcess) {
          setPendingReviewStatus("DENIED");
        }

        setUIModalState("error-summary");
      }
      // show modal with errors;
    }
  }, [performFlowConnectionsCheck, isInApprovalProcess]);

  const performSendToApproval = useCallback(async () => {
    try {
      await sendFlowToApproval();
      revalidator.revalidate();
      showToast({
        title: "Flujo enviado a aprobación",
        description: "El flujo ha sido enviado a aprobación correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    } catch (error) {
      console.error("Error sending flow to approval:", error);
      showToast({
        title: "Error al enviar el flujo a aprobación",
        description:
          "Ha ocurrido un error al intentar enviar el flujo a aprobación.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    }
  }, [sendFlowToApproval, revalidator, showToast]);

  const [error, setError] = useState<AxiosError<ApiErrorResponse> | null>(null);

  const lockFlow = useCallback(async () => {
    try {
      await updateFlow({
        editing: !(data?.data?.editing ?? false),
      });
      revalidator.revalidate();
      showToast({
        title: data?.data?.editing ? "Flujo desbloqueado" : "Flujo bloqueado",
        description: data?.data?.editing
          ? "El flujo ha sido desbloqueado y ya puede ser editado por otros usuarios."
          : "El flujo ha sido bloqueado para edición.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    } catch (error) {
      setError(error as AxiosError<ApiErrorResponse>);
      showToast({
        title: "Error al bloquear el flujo",
        description: "Ha ocurrido un error al intentar bloquear el flujo.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    }
  }, [data?.data?.editing]);

  const onCompleteRevision = async ({
    comment,
    type,
  }: {
    comment: string;
    type: "APPROVED" | "DENIED";
  }) => {
    try {
      const lastestRevision = data?.data.revisions?.reduce((prev, current) => {
        return prev.created_at > current.created_at ? prev : current;
      });
      if (!lastestRevision) {
        return;
      }
      await updateFlowRevision({
        action: type,
        comments: [comment],
        revisionId: lastestRevision?.id || "",
      });
      revalidator.revalidate();
      showToast({
        title: "Resolución enviada",
        description:
          "La resolución de la revisión ha sido enviada correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
      setModalState("idle");
    } catch (error) {
      console.error("Error completing flow revision:", error);
      showToast({
        title: "Error al enviar la resolución",
        description:
          "Ha ocurrido un error al intentar enviar la resolución de la revisión.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    }
  };

  const navigate = useNavigate();
  const viewExecutions = () => {
    navigate(`/procesos/${params.id}/flujo/${params.flowId}/ejecuciones`);
  };

  useEffect(() => {
    if (data?.data.is_duplicating) return;
    const isValidForApprovment = !!data?.data?.last_checked_at;

    const showSendToApprovalButton =
      (isValidForApprovment &&
        data?.data?.status === "DRAFT" &&
        data?.data?.review_status === "NOT_STARTED") ||
      data?.data?.review_status === "DENIED";
    const showStartReviewButton = false;
    // const showStartReviewButton = data?.data?.review_status === "PENDING";
    const showSendResolutionButton = data?.data?.review_status === "IN_PROCESS";

    // const canValidateFlow =
    //   data?.data?.review_status === "PENDING" ||
    //   data?.data?.review_status === "IN_PROCESS";
    setComp(
      <>
        {isFlowBlocked && (
          <Badge size="xs" variant="warning">
            Edición bloqueada
          </Badge>
        )}
        {/* <Button
          type="button"
          variant="quiet"
          size="icon"
          onClick={() => {
            setUIModalState("search-nodes");
          }}
          aria-label={"Buscar nodos"}
        >
          <Icon type="far" name="magnifying-glass" />
        </Button> */}
        <Button
          type="button"
          variant="quiet"
          size="icon"
          onClick={lockFlow}
          isLoading={isPerformingFlowConnectionsCheck}
          aria-label={
            data?.data?.editing ? "Desbloquear flujo" : "Bloquear flujo"
          }
        >
          <Icon type="far" name={data?.data?.editing ? "lock-open" : "lock"} />
        </Button>
        <Button
          size="sm"
          type="button"
          variant="quiet"
          onClick={viewExecutions}
        >
          Ver ejecuciones
          <Icon type="far" name="arrow-up-right" />
        </Button>
        {!data?.data?.editing && (
          <>
            <Button
              type="button"
              variant="quiet"
              size="sm"
              onClick={performConnectionCheck}
              isLoading={isPerformingFlowConnectionsCheck}
              //disabled={isFlowBlocked}
              disabled={
                data?.data?.status === "PUBLISHED" ||
                data?.data?.status === "DEPRECATED"
              }
            >
              Validar flujo
              <Icon type="far" name="rotate-exclamation" />
            </Button>
            {/* <Button
              type="button"
              variant="quiet"
              size="sm"
              disabled={
                !canTestNodes(nodes, edges) || isPerformingFlowConnectionsCheck
              }
            >
              Probar flujo
              <Icon type="far" name="play" />
            </Button> */}
            {data?.data?.parent_flow_id === null && (
              <>
                {showSendToApprovalButton && (
                  <Button
                    size="sm"
                    type="button"
                    disabled={!isValidForApprovment}
                    onClick={performSendToApproval}
                    isLoading={isSendingFlowToApproval}
                  >
                    Enviar a aprobación
                    <Icon type="far" name="arrow-up-right" />
                  </Button>
                )}
                {showStartReviewButton && (
                  <Button
                    size="sm"
                    type="button"
                    onClick={performStartRevision}
                    isLoading={isLoadingUpdateFlowRevision}
                  >
                    Empezar revisión
                    <Icon type="far" name="arrow-up-right" />
                  </Button>
                )}
                {showSendResolutionButton && canPerformReview && (
                  <Button
                    size="sm"
                    type="button"
                    onClick={() => {
                      setModalState("make-revision");
                    }}
                  >
                    Mandar resolución
                    <Icon type="far" name="arrow-up-right" />
                  </Button>
                )}
              </>
            )}
            <Menu
              isFlowBlocked={isFlowBlocked}
              onEdit={() => {
                setModalState("edit");
              }}
              onShowResolutionHistory={() => {
                setModalState("show-resolution-history");
              }}
            />
          </>
        )}
      </>,
    );

    return () => {
      setComp(null);
    };
  }, [
    setComp,
    flowName,
    nodes,
    edges,
    isLoadingUpdateFlow,
    data,
    isFlowBlocked,
    performConnectionCheck,
    isPerformingFlowConnectionsCheck,
    isSendingFlowToApproval,
    performStartRevision,
    isLoadingUpdateFlowRevision,
    canPerformReview,
    isInApprovalProcess,
  ]);

  useEffect(() => {
    if (pendingReviewStatus && uimodal === "idle") {
      updateFlow({ review_status: pendingReviewStatus });
      setPendingReviewStatus(null);
    }
  }, [uimodal, pendingReviewStatus, updateFlow]);

  if (data?.data?.is_duplicating) {
    return (
      <StatusPage
        statusCode={404}
        title="Flujo en duplicación"
        description="El flujo que intentas acceder está bajo un proceso de duplicación. Por favor vuelve más tarde."
        actionText="Volver al inicio"
        actionLink={`/procesos/${params.id}`}
      />
    );
  }

  return (
    <>
      {modalState === "make-revision" && (
        <FlowResolutionModal
          open
          onClose={() => {
            setError(null);
            setModalState("idle");
          }}
          onSubmit={(data) => {
            onCompleteRevision(data);
          }}
          isLoading={isLoadingUpdateFlowRevision}
        />
      )}
      {modalState === "show-resolution-history" && (
        <FlowResolutionDetail
          data={data?.data?.revisions || []}
          onClose={() => {
            setModalState("idle");
          }}
        />
      )}
      {modalState === "edit" && data?.data?.parent_flow_id !== null && (
        <EditFlowModal
          open
          onClose={() => {
            setError(null);
            setModalState("idle");
          }}
          onSubmit={(data) => {
            onSaveFlow(data);
          }}
          isLoading={isLoadingUpdateFlow}
          processId={params.id!}
          defaultValues={{
            name: data?.data.name || "",
            parent_flow_id: data?.data.parent_flow_id || "",
          }}
          error={error as AxiosError<ApiErrorResponse> | undefined}
        />
      )}
      {modalState === "edit" && data?.data?.parent_flow_id === null && (
        <EditParentFlowModal
          open
          onClose={() => {
            setError(null);
            setModalState("idle");
          }}
          onSubmit={(data) => {
            onSaveFlow(data);
          }}
          isLoading={isLoadingUpdateFlow}
          processId={params.id!}
          defaultValues={{
            name: data?.data.name || "",
            extension_count: data?.data.max_time_extensions || 0,
          }}
          error={error as AxiosError<ApiErrorResponse> | undefined}
        />
      )}
      <ConfirmationModalBase
        acceptText="Salir sin guardar"
        cancelText="Cancelar"
        description="¿Estás seguro de que deseas salir? Todos los cambios que hayas realizado se perderán"
        title="Salir sin guardar"
        isOpen={blocker.state === "blocked"}
        onAccept={() => {
          reset();
          setFlowName("");
          blocker?.proceed?.();
        }}
        onClose={() => {
          blocker?.reset?.();
        }}
        acceptVariant="danger-secondary"
      />
      <WorkflowCanvas />
    </>
  );
};

export default EditFlowPage;

const Menu = ({
  isFlowBlocked,
  onEdit,
  onShowResolutionHistory,
}: {
  isFlowBlocked: boolean;
  onEdit?: () => void;
  onShowResolutionHistory?: () => void;
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="quiet" size="icon" aria-label="Opciones" type="button">
          <Icon type="far" name="ellipsis-vertical" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={cn(
          "flex flex-col rounded-lg border-[0.5px] border-neutral bg-neutral-object text-body-loud font-semibold shadow-neutral-xl",
        )}
        side="bottom"
        align="end"
      >
        {!isFlowBlocked && (
          <DropdownMenuItem
            className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
            asChild
          >
            <button type="button" className="w-full" onClick={onEdit}>
              Editar detalles
            </button>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
          asChild
        >
          <button
            type="button"
            className="w-full"
            onClick={onShowResolutionHistory}
          >
            Historial de resoluciones
          </button>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
